import React, { useState, useCallback, useEffect, useRef } from 'react';
import '@/App.css';
import { ThemeProvider } from 'next-themes';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Menu, Play, Moon, Sun, Monitor, Plus, PlayCircle, Download, Upload, Copy, Lock, Crown, Settings, Box } from 'lucide-react';
import { useTheme } from 'next-themes';

// Auth Context and Components
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { AuthModal } from '@/components/AuthModal';
import { AuthCallback } from '@/components/AuthCallback';
import { PricingModal } from '@/components/PricingModal';
import { UserMenu } from '@/components/UserMenu';

// Tab Components
import { PileMakeupTab } from '@/components/tabs/PileMakeupTab';
import { SoilProfileTab } from '@/components/tabs/SoilProfileTab';
import { ResultsTab } from '@/components/tabs/ResultsTab';
import { SensitivityTab } from '@/components/tabs/SensitivityTab';
import { TechnicalReportTab } from '@/components/tabs/TechnicalReportTab';
import { OverviewTab } from '@/components/tabs/OverviewTab';
import { Visualization3DTab } from '@/components/tabs/Visualization3DTab';
import { InputSummary } from '@/components/InputSummary';
import { SettingsPanel } from '@/components/SettingsPanel';

// Calculations
import { calculateCapacity, calculateFOS, DEFAULT_FOS_TARGETS, updateFOSTargets } from '@/lib/calculations';

// Default pile template - WHP-3 reference case calibrated values
const createDefaultPile = (id, position = { x: 0, y: 0 }) => ({
  id,
  name: `P-${String(id).padStart(2, '0')}`,
  position,
  pileInputs: {
    steelYieldStrength: 355,
    groutLength: 23.5,
    groutStrength: 45,
    steelGroutBondMode: 'manual', // 'auto' | 'manual'
    steelGroutBondManual: 0.2,
    holeOversize: 150,
    pileSections: [
      { id: 1, diameter: 762, wallThickness: 25, length: 23.5 }
    ]
  },
  soilLayers: [
    { id: 1, from: 0, to: 1.5, qu: 0.5, gamma: 22, rqd: 40, trg: 0.00 },
    { id: 2, from: 1.5, to: 9.2, qu: 1.0, gamma: 23, rqd: 55, trg: 0.10 },
    { id: 3, from: 9.2, to: 21.4, qu: 2.0, gamma: 24, rqd: 65, trg: 0.20 },
    { id: 4, from: 21.4, to: 23.5, qu: 2.0, gamma: 23, rqd: 50, trg: 0.20 }
  ],
  designLoads: {
    compOperating: 0,
    compStorm: 0,
    tensOperating: 0,
    tensStorm: 0,
    compOperatingCaseId: '',
    compStormCaseId: '',
    tensOperatingCaseId: '',
    tensStormCaseId: ''
  },
  results: null,
  fos: null
});

// Create initial piles with grid layout positions
const createInitialPiles = () => {
  const piles = [];
  const positions = [
    { x: 50, y: 50 }, { x: 150, y: 50 }, { x: 250, y: 50 },
    { x: 50, y: 150 }, { x: 150, y: 150 }, { x: 250, y: 150 }
  ];
  
  for (let i = 1; i <= 6; i++) {
    piles.push(createDefaultPile(i, positions[i - 1]));
  }
  return piles;
};

// Theme Toggle Component
const ThemeToggle = () => {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return (
    <div className="flex items-center gap-1 border border-border rounded-md p-1">
      <Button
        variant={theme === 'light' ? 'secondary' : 'ghost'}
        size="icon"
        className="h-7 w-7"
        onClick={() => setTheme('light')}
        data-testid="theme-light-btn"
      >
        <Sun className="h-4 w-4" />
      </Button>
      <Button
        variant={theme === 'system' ? 'secondary' : 'ghost'}
        size="icon"
        className="h-7 w-7"
        onClick={() => setTheme('system')}
        data-testid="theme-system-btn"
      >
        <Monitor className="h-4 w-4" />
      </Button>
      <Button
        variant={theme === 'dark' ? 'secondary' : 'ghost'}
        size="icon"
        className="h-7 w-7"
        onClick={() => setTheme('dark')}
        data-testid="theme-dark-btn"
      >
        <Moon className="h-4 w-4" />
      </Button>
    </div>
  );
};

// Main App Content
const AppContent = () => {
  const { user, loading: authLoading, isAuthenticated, isPro, canAccessFeature } = useAuth();
  
  const [activeTab, setActiveTab] = useState('overview');
  const [piles, setPiles] = useState(createInitialPiles);
  const [activePileId, setActivePileId] = useState(1);
  const [sensitivityResults, setSensitivityResults] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [pricingTrigger, setPricingTrigger] = useState('');
  const chartRef = useRef(null);
  const sensitivityChartRef = useRef(null);
  const projectFileInputRef = useRef(null);

  // New state for FOS targets and end bearing
  const [fosTargets, setFosTargets] = useState({ ...DEFAULT_FOS_TARGETS });
  const [endBearingConfig, setEndBearingConfig] = useState({
    enabled: false,
    method: 'abbsNeedham', // 'abbsNeedham' | 'api'
    params: {
      soilType: 'rock',
      su: 100,
      frictionAngle: 30,
      effectiveStress: 200
    }
  });

  // Report metadata and narrative state (persisted across tab changes)
  const [reportMetadata, setReportMetadata] = useState({
    companyName: 'Darkocean SPC LLC',
    logoUrl: '',
    projectName: '',
    client: '',
    location: '',
    pileId: '',
    preparedBy: '',
    checkedBy: '',
    date: new Date().toISOString().split('T')[0],
    revision: '0'
  });

  const [reportNarrative, setReportNarrative] = useState({
    executiveSummary: '',
    designAssumptions: '',
    recommendations: ''
  });

  const [reportAppendices, setReportAppendices] = useState([]);

  // Update global FOS targets when state changes
  useEffect(() => {
    updateFOSTargets(fosTargets);
  }, [fosTargets]);

  // Reset FOS targets to defaults
  const resetFOSTargets = () => {
    setFosTargets({ ...DEFAULT_FOS_TARGETS });
  };

  // Check if we're on auth callback route
  const isAuthCallback = window.location.pathname === '/auth/callback';

  // Show auth modal if not authenticated (after loading)
  useEffect(() => {
    if (!authLoading && !isAuthenticated && !isAuthCallback) {
      setShowAuthModal(true);
    }
  }, [authLoading, isAuthenticated, isAuthCallback]);

  // Handle tab change with plan enforcement
  const handleTabChange = (tab) => {
    // Pro-only tabs
    const proOnlyTabs = ['sensitivity', 'report'];
    
    if (proOnlyTabs.includes(tab) && !isPro) {
      setPricingTrigger(tab === 'sensitivity' ? 'Sensitivity Analysis' : 'Technical Report & PDF Export');
      setShowPricingModal(true);
      return;
    }
    
    setActiveTab(tab);
  };

  // Get active pile
  const activePile = piles.find(p => p.id === activePileId) || piles[0];

  // Update active pile data
  const updateActivePile = useCallback((field, value) => {
    setPiles(prevPiles => prevPiles.map(pile => 
      pile.id === activePileId ? { ...pile, [field]: value } : pile
    ));
  }, [activePileId]);

  // Set pile inputs
  const setPileInputs = useCallback((updater) => {
    setPiles(prevPiles => prevPiles.map(pile => {
      if (pile.id !== activePileId) return pile;
      const newInputs = typeof updater === 'function' ? updater(pile.pileInputs) : updater;
      return { ...pile, pileInputs: newInputs };
    }));
  }, [activePileId]);

  // Set soil layers
  const setSoilLayers = useCallback((updater) => {
    setPiles(prevPiles => prevPiles.map(pile => {
      if (pile.id !== activePileId) return pile;
      const newLayers = typeof updater === 'function' ? updater(pile.soilLayers) : updater;
      return { ...pile, soilLayers: newLayers };
    }));
  }, [activePileId]);

  // Set design loads and recalculate FOS
  const setDesignLoads = useCallback((updater) => {
    setPiles(prevPiles => prevPiles.map(pile => {
      if (pile.id !== activePileId) return pile;
      const newLoads = typeof updater === 'function' ? updater(pile.designLoads) : updater;
      
      // Recalculate FOS if we have results
      let newFos = pile.fos;
      if (pile.results) {
        newFos = calculateFOS(pile.results.ultimateCapacity, newLoads);
      }
      
      return { ...pile, designLoads: newLoads, fos: newFos };
    }));
  }, [activePileId]);

  // Run analysis for a single pile
  const runPileAnalysis = useCallback((pileId) => {
    setPiles(prevPiles => prevPiles.map(pile => {
      if (pile.id !== pileId) return pile;
      
      try {
        const inputs = {
          ...pile.pileInputs,
          soilLayers: pile.soilLayers,
          // Include end bearing config
          endBearingEnabled: endBearingConfig.enabled,
          endBearingMethod: endBearingConfig.method,
          endBearingParams: endBearingConfig.params
        };
        
        const results = calculateCapacity(inputs);
        const fos = calculateFOS(results.ultimateCapacity, pile.designLoads, fosTargets);
        
        return { ...pile, results, fos };
      } catch (error) {
        console.error(`Calculation error for pile ${pile.name}:`, error);
        return pile;
      }
    }));
  }, [endBearingConfig, fosTargets]);

  // Run analysis for active pile
  const runAnalysis = useCallback(() => {
    setIsAnalyzing(true);
    
    try {
      runPileAnalysis(activePileId);
      const pile = piles.find(p => p.id === activePileId);
      
      if (pile) {
        const inputs = { 
          ...pile.pileInputs, 
          soilLayers: pile.soilLayers,
          endBearingEnabled: endBearingConfig.enabled,
          endBearingMethod: endBearingConfig.method,
          endBearingParams: endBearingConfig.params
        };
        const results = calculateCapacity(inputs);
        
        let description = `${pile.name}: Ultimate Capacity = ${results.ultimateCapacity.toFixed(3)} MN`;
        if (endBearingConfig.enabled && results.endBearingCapacity > 0) {
          description += ` (incl. ${results.endBearingCapacity.toFixed(3)} MN end bearing)`;
        }
        
        toast.success('Analysis Complete', { description });
      }
      
      setActiveTab('results');
    } catch (error) {
      console.error('Calculation error:', error);
      toast.error('Analysis Failed', {
        description: error.message || 'Please check your inputs'
      });
    } finally {
      setIsAnalyzing(false);
    }
  }, [activePileId, piles, runPileAnalysis, endBearingConfig]);

  // Run analysis for all piles
  const runAllAnalysis = useCallback(() => {
    setIsAnalyzing(true);
    
    try {
      piles.forEach(pile => runPileAnalysis(pile.id));
      
      toast.success('All Piles Analyzed', {
        description: `${piles.length} piles calculated successfully`
      });
      
      setActiveTab('overview');
    } catch (error) {
      console.error('Calculation error:', error);
      toast.error('Analysis Failed', {
        description: error.message || 'Please check your inputs'
      });
    } finally {
      setIsAnalyzing(false);
    }
  }, [piles, runPileAnalysis]);

  // Add new pile
  const addPile = useCallback(() => {
    const maxId = Math.max(...piles.map(p => p.id), 0);
    const newId = maxId + 1;
    const gridPos = {
      x: 50 + ((newId - 1) % 4) * 100,
      y: 50 + Math.floor((newId - 1) / 4) * 100
    };
    const newPile = createDefaultPile(newId, gridPos);
    setPiles(prev => [...prev, newPile]);
    setActivePileId(newId);
    toast.success(`Pile ${newPile.name} added`);
  }, [piles]);

  // Delete pile
  const deletePile = useCallback((pileId) => {
    if (piles.length <= 1) {
      toast.error('Cannot delete', { description: 'At least one pile is required' });
      return;
    }
    
    const pileToDelete = piles.find(p => p.id === pileId);
    setPiles(prev => prev.filter(p => p.id !== pileId));
    
    // If deleting active pile, switch to first remaining pile
    if (pileId === activePileId) {
      const remainingPiles = piles.filter(p => p.id !== pileId);
      if (remainingPiles.length > 0) {
        setActivePileId(remainingPiles[0].id);
      }
    }
    
    toast.success(`Pile ${pileToDelete?.name || pileId} deleted`);
  }, [piles, activePileId]);

  // Update pile position (for drag-and-drop)
  const updatePilePosition = useCallback((pileId, newPosition) => {
    setPiles(prev => prev.map(pile => 
      pile.id === pileId ? { ...pile, position: newPosition } : pile
    ));
  }, []);

  // Export piles to CSV
  const exportPilesToCSV = useCallback(() => {
    const headers = [
      'Pile ID', 'Position X', 'Position Y',
      'Diameter (mm)', 'Wall (mm)', 'Length (m)',
      'Steel Yield (MPa)', 'Grout Length (m)', 'Grout Strength (MPa)', 'Steel-Grout Bond (MPa)',
      'Comp Operating (MN)', 'Comp Storm (MN)', 'Tens Operating (MN)', 'Tens Storm (MN)',
      'Comp Op Case ID', 'Comp Storm Case ID', 'Tens Op Case ID', 'Tens Storm Case ID',
      'Ultimate Qu (MN)', 'Allowable Qa (MN)', 'Min FS', 'Governing Case'
    ];
    
    const rows = piles.map(pile => {
      const section = pile.pileInputs.pileSections[0] || {};
      return [
        pile.name,
        pile.position.x,
        pile.position.y,
        section.diameter || 762,
        section.wallThickness || 25,
        section.length || 35,
        pile.pileInputs.steelYieldStrength,
        pile.pileInputs.groutLength,
        pile.pileInputs.groutStrength,
        pile.pileInputs.steelGroutBond,
        pile.designLoads.compOperating || '',
        pile.designLoads.compStorm || '',
        pile.designLoads.tensOperating || '',
        pile.designLoads.tensStorm || '',
        pile.designLoads.compOperatingCaseId || '',
        pile.designLoads.compStormCaseId || '',
        pile.designLoads.tensOperatingCaseId || '',
        pile.designLoads.tensStormCaseId || '',
        pile.results?.ultimateCapacity?.toFixed(3) || '',
        pile.results?.allowableCapacity?.toFixed(3) || '',
        pile.fos?.minFS?.toFixed(2) || '',
        pile.fos?.governingCase || ''
      ];
    });
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pile_configurations.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Export Complete', { description: `${piles.length} piles exported to CSV` });
  }, [piles]);

  // Import piles from CSV
  const importPilesFromCSV = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const headers = lines[0].split(',');
        
        const importedPiles = lines.slice(1).map((line, index) => {
          const values = line.split(',');
          const pileId = index + 1;
          
          return {
            id: pileId,
            name: values[0] || `P-${String(pileId).padStart(2, '0')}`,
            position: {
              x: parseFloat(values[1]) || 50 + ((pileId - 1) % 4) * 100,
              y: parseFloat(values[2]) || 50 + Math.floor((pileId - 1) / 4) * 100
            },
            pileInputs: {
              steelYieldStrength: parseFloat(values[6]) || 355,
              groutLength: parseFloat(values[7]) || 23.5,
              groutStrength: parseFloat(values[8]) || 45,
              steelGroutBondMode: 'manual',
              steelGroutBondManual: parseFloat(values[9]) || 0.2,
              holeOversize: 150,
              pileSections: [{
                id: 1,
                diameter: parseFloat(values[3]) || 762,
                wallThickness: parseFloat(values[4]) || 25,
                length: parseFloat(values[5]) || 23.5
              }]
            },
            soilLayers: [
              { id: 1, from: 0, to: 1.5, qu: 0.5, gamma: 22, rqd: 40, trg: 0.00 },
              { id: 2, from: 1.5, to: 9.2, qu: 1.0, gamma: 23, rqd: 55, trg: 0.10 },
              { id: 3, from: 9.2, to: 21.4, qu: 2.0, gamma: 24, rqd: 65, trg: 0.20 },
              { id: 4, from: 21.4, to: 23.5, qu: 2.0, gamma: 23, rqd: 50, trg: 0.20 }
            ],
            designLoads: {
              compOperating: parseFloat(values[10]) || 0,
              compStorm: parseFloat(values[11]) || 0,
              tensOperating: parseFloat(values[12]) || 0,
              tensStorm: parseFloat(values[13]) || 0,
              compOperatingCaseId: values[14] || '',
              compStormCaseId: values[15] || '',
              tensOperatingCaseId: values[16] || '',
              tensStormCaseId: values[17] || ''
            },
            results: null,
            fos: null
          };
        });
        
        if (importedPiles.length > 0) {
          setPiles(importedPiles);
          setActivePileId(importedPiles[0].id);
          toast.success('Import Complete', { description: `${importedPiles.length} piles imported` });
        }
      } catch (error) {
        toast.error('Import Failed', { description: 'Invalid CSV format' });
      }
    };
    reader.readAsText(file);
  }, []);

  // Set active pile and navigate to results
  const selectPileAndNavigate = useCallback((pileId) => {
    setActivePileId(pileId);
    setActiveTab('results');
  }, []);

  // Save project to JSON file
  const saveProject = useCallback(() => {
    const projectData = {
      version: '1.4.0',
      savedAt: new Date().toISOString(),
      piles: piles.map(pile => ({
        id: pile.id,
        name: pile.name,
        position: pile.position,
        pileInputs: pile.pileInputs,
        soilLayers: pile.soilLayers,
        designLoads: pile.designLoads
      })),
      scenarios: scenarios.map(s => ({
        id: s.id,
        name: s.name,
        pileId: s.pileId,
        savedAt: s.savedAt,
        includeInReport: s.includeInReport,
        pileInputs: s.pileInputs,
        soilLayers: s.soilLayers,
        designLoads: s.designLoads,
        results: s.results,
        fos: s.fos,
        narrative: s.narrative,
        metadata: s.metadata
      }))
    };
    
    const blob = new Blob([JSON.stringify(projectData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `grouted_pile_project_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Project Saved', { description: `${piles.length} piles and ${scenarios.length} scenarios saved` });
  }, [piles, scenarios]);

  // Load project from JSON file
  const loadProject = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const projectData = JSON.parse(e.target.result);
        
        // Validate structure
        if (!projectData.piles || !Array.isArray(projectData.piles)) {
          throw new Error('Invalid project file format');
        }
        
        // Reconstruct piles with results cleared
        const loadedPiles = projectData.piles.map(pile => ({
          ...pile,
          results: null,
          fos: null
        }));
        
        // Load scenarios if present
        if (projectData.scenarios && Array.isArray(projectData.scenarios)) {
          setScenarios(projectData.scenarios);
        }
        
        if (loadedPiles.length > 0) {
          setPiles(loadedPiles);
          setActivePileId(loadedPiles[0].id);
          const scenarioCount = projectData.scenarios?.length || 0;
          toast.success('Project Loaded', { 
            description: `${loadedPiles.length} piles${scenarioCount > 0 ? ` and ${scenarioCount} scenarios` : ''} loaded` 
          });
        }
      } catch (error) {
        toast.error('Load Failed', { description: 'Invalid project file format' });
      }
    };
    reader.readAsText(file);
  }, []);

  // Copy pile data to all piles
  const copyPileToAll = useCallback((dataType) => {
    const sourcePile = piles.find(p => p.id === activePileId);
    if (!sourcePile) return;

    setPiles(prevPiles => prevPiles.map(pile => {
      if (pile.id === activePileId) return pile;
      
      if (dataType === 'pileInputs') {
        return { ...pile, pileInputs: JSON.parse(JSON.stringify(sourcePile.pileInputs)), results: null, fos: null };
      } else if (dataType === 'soilLayers') {
        return { ...pile, soilLayers: JSON.parse(JSON.stringify(sourcePile.soilLayers)), results: null, fos: null };
      }
      return pile;
    }));

    const dataName = dataType === 'pileInputs' ? 'Pile Makeup' : 'Soil Profile';
    toast.success(`${dataName} Copied`, { description: `Applied to all ${piles.length - 1} other piles` });
  }, [piles, activePileId]);

  // Auto-run analysis on mount for first pile
  useEffect(() => {
    runPileAnalysis(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sidebar content
  const SidebarContent = () => (
    <div className="h-full flex flex-col">
      <div className="sidebar-header">
        <img 
          src="https://customer-assets.emergentagent.com/job_carbonate-pile-eng/artifacts/fdtizxl5_Darkocean%20logo%20Secondary.png"
          alt="Darkocean"
          className="h-8 w-auto mb-3 dark:invert-0 invert"
          data-testid="sidebar-logo"
        />
        <h2 className="font-semibold text-lg text-foreground">Input Summary</h2>
        <p className="text-xs text-muted-foreground mt-1">Pile: {activePile?.name}</p>
        {user && (
          <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
            Plan: <span className={isPro ? 'text-amber-500 font-medium' : ''}>{isPro ? 'Pro' : 'Free'}</span>
            {isPro && <Crown className="h-3 w-3 text-amber-500" />}
          </div>
        )}
      </div>
      <ScrollArea className="flex-1">
        <div className="sidebar-content">
          <InputSummary 
            pileInputs={activePile?.pileInputs} 
            soilLayers={activePile?.soilLayers} 
            results={activePile?.results}
            designLoads={activePile?.designLoads}
            fos={activePile?.fos}
          />
        </div>
      </ScrollArea>
    </div>
  );

  // Show auth callback if on that route
  if (isAuthCallback) {
    return <AuthCallback onComplete={() => window.location.href = '/'} />;
  }

  // Show loading state
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="h-12 w-12 mx-auto border-4 border-[#00284F] border-t-transparent rounded-full animate-spin"></div>
          <p className="mt-4 text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      {/* Auth Modal */}
      <AuthModal open={showAuthModal && !isAuthenticated} onOpenChange={setShowAuthModal} />
      
      {/* Pricing Modal */}
      <PricingModal 
        open={showPricingModal} 
        onOpenChange={setShowPricingModal} 
        trigger={pricingTrigger}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar */}
        <aside className="sidebar hidden lg:block">
          <SidebarContent />
        </aside>

        {/* Main Content */}
        <main className="main-content">
          {/* Header */}
          <header className="app-header">
            <div className="flex items-center gap-2 md:gap-4 min-w-0">
              {/* Mobile sidebar trigger */}
              <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="ghost" size="icon" className="flex-shrink-0" data-testid="mobile-menu-btn">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[320px] p-0">
                  <SidebarContent />
                </SheetContent>
              </Sheet>

              {/* Darkocean Logo - hidden on desktop since it's in sidebar */}
              <img 
                src="https://customer-assets.emergentagent.com/job_carbonate-pile-eng/artifacts/fdtizxl5_Darkocean%20logo%20Secondary.png"
                alt="Darkocean"
                className="h-8 md:h-10 w-auto dark:invert-0 invert lg:hidden flex-shrink-0"
                data-testid="header-logo"
              />

              <div className="min-w-0">
                <h1 className="app-title text-sm md:text-base lg:text-lg truncate flex items-center gap-2">
                  Darkocean Pile Cap Pro
                  {isPro && <Crown className="h-4 w-4 text-amber-500" />}
                </h1>
                <p className="app-subtitle text-xs hidden sm:block">Abbs & Needham Method (OTC-4852, 1985)</p>
              </div>
            </div>

            <div className="flex items-center gap-1 md:gap-3 flex-shrink-0">
              {/* User Menu */}
              {isAuthenticated && (
                <UserMenu onUpgradeClick={() => setShowPricingModal(true)} />
              )}
              
              {/* Project Save/Load - hidden on very small screens */}
              <input
                ref={projectFileInputRef}
                type="file"
                accept=".json"
                className="hidden"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    loadProject(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                data-testid="project-file-input"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={saveProject}
                title="Save Project"
                className="hidden sm:flex"
                data-testid="save-project-btn"
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => projectFileInputRef.current?.click()}
                title="Load Project"
                className="hidden sm:flex"
                data-testid="load-project-btn"
              >
                <Upload className="h-4 w-4" />
              </Button>

              {/* Pile Selector */}
              <div className="flex items-center gap-1 md:gap-2">
                <Select value={String(activePileId)} onValueChange={(v) => setActivePileId(Number(v))}>
                  <SelectTrigger className="w-[80px] md:w-[120px]" data-testid="pile-selector">
                    <SelectValue placeholder="Select Pile" />
                  </SelectTrigger>
                  <SelectContent>
                    {piles.map(pile => (
                      <SelectItem key={pile.id} value={String(pile.id)}>
                        {pile.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={addPile} data-testid="add-pile-btn">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Theme Toggle - hidden on mobile */}
              <div className="hidden md:block">
                <ThemeToggle />
              </div>
              
              {/* Run Analysis Buttons */}
              <Button
                onClick={runAnalysis}
                disabled={isAnalyzing}
                size="sm"
                className="bg-[#00284F] hover:bg-[#00284F]/90 text-white text-xs md:text-sm"
                data-testid="run-analysis-btn"
              >
                <Play className="h-3 w-3 md:h-4 md:w-4 md:mr-2" />
                <span className="hidden md:inline">{isAnalyzing ? 'Analyzing...' : 'Run Analysis'}</span>
              </Button>
              <Button
                onClick={runAllAnalysis}
                disabled={isAnalyzing}
                variant="outline"
                size="sm"
                className="hidden sm:flex text-xs md:text-sm"
                data-testid="run-all-btn"
              >
                <PlayCircle className="h-3 w-3 md:h-4 md:w-4 md:mr-2" />
                <span className="hidden md:inline">Run All</span>
              </Button>
            </div>
          </header>

          {/* Tabs Content */}
          <div className="tab-content-area">
            <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
              <TabsList className="mb-6 bg-muted/50 flex-wrap" data-testid="main-tabs">
                <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                <TabsTrigger value="pile-makeup" data-testid="tab-pile-makeup">Pile Makeup</TabsTrigger>
                <TabsTrigger value="soil-profile" data-testid="tab-soil-profile">Soil Profile</TabsTrigger>
                <TabsTrigger value="results" data-testid="tab-results">Results</TabsTrigger>
                <TabsTrigger value="3d-view" data-testid="tab-3d-view" className="flex items-center gap-1">
                  <Box className="h-3 w-3" />
                  3D View
                </TabsTrigger>
                <TabsTrigger value="settings" data-testid="tab-settings" className="flex items-center gap-1">
                  <Settings className="h-3 w-3" />
                  Settings
                </TabsTrigger>
                <TabsTrigger value="sensitivity" data-testid="tab-sensitivity" className="flex items-center gap-1">
                  Sensitivity
                  {!isPro && <Lock className="h-3 w-3 text-muted-foreground" />}
                </TabsTrigger>
                <TabsTrigger value="report" data-testid="tab-report" className="flex items-center gap-1">
                  Technical Report
                  {!isPro && <Lock className="h-3 w-3 text-muted-foreground" />}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="fade-in">
                <OverviewTab 
                  piles={piles}
                  activePileId={activePileId}
                  onSelectPile={selectPileAndNavigate}
                  onDeletePile={deletePile}
                  onUpdatePosition={updatePilePosition}
                  onExportCSV={exportPilesToCSV}
                  onImportCSV={importPilesFromCSV}
                />
              </TabsContent>

              <TabsContent value="pile-makeup" className="fade-in">
                <PileMakeupTab
                  pileInputs={activePile?.pileInputs}
                  setPileInputs={setPileInputs}
                  results={activePile?.results}
                  onCopyToAll={() => copyPileToAll('pileInputs')}
                  pileCount={piles.length}
                />
              </TabsContent>

              <TabsContent value="soil-profile" className="fade-in">
                <SoilProfileTab
                  pileInputs={activePile?.pileInputs}
                  setPileInputs={setPileInputs}
                  soilLayers={activePile?.soilLayers}
                  setSoilLayers={setSoilLayers}
                  onCopyToAll={() => copyPileToAll('soilLayers')}
                  pileCount={piles.length}
                />
              </TabsContent>

              <TabsContent value="results" className="fade-in">
                <ResultsTab 
                  results={activePile?.results} 
                  chartRef={chartRef}
                  designLoads={activePile?.designLoads}
                  setDesignLoads={setDesignLoads}
                  fos={activePile?.fos}
                  pileName={activePile?.name}
                  piles={piles}
                  activePileId={activePileId}
                  onSelectPile={setActivePileId}
                  fosTargets={fosTargets}
                />
              </TabsContent>

              <TabsContent value="3d-view" className="fade-in">
                <Visualization3DTab
                  piles={piles}
                  activePileId={activePileId}
                  onSelectPile={setActivePileId}
                />
              </TabsContent>

              <TabsContent value="settings" className="fade-in">
                <SettingsPanel
                  fosTargets={fosTargets}
                  setFosTargets={setFosTargets}
                  endBearingConfig={endBearingConfig}
                  setEndBearingConfig={setEndBearingConfig}
                  onResetFOS={resetFOSTargets}
                />
              </TabsContent>

              <TabsContent value="sensitivity" className="fade-in">
                <SensitivityTab
                  pileInputs={activePile?.pileInputs}
                  soilLayers={activePile?.soilLayers}
                  sensitivityResults={sensitivityResults}
                  setSensitivityResults={setSensitivityResults}
                  chartRef={sensitivityChartRef}
                />
              </TabsContent>

              <TabsContent value="report" className="fade-in">
                <TechnicalReportTab
                  pileInputs={activePile?.pileInputs}
                  soilLayers={activePile?.soilLayers}
                  results={activePile?.results}
                  sensitivityResults={sensitivityResults}
                  chartRef={chartRef}
                  sensitivityChartRef={sensitivityChartRef}
                  designLoads={activePile?.designLoads}
                  fos={activePile?.fos}
                  scenarios={scenarios}
                  setScenarios={setScenarios}
                  activePileName={activePile?.name}
                  metadata={reportMetadata}
                  setMetadata={setReportMetadata}
                  narrative={reportNarrative}
                  setNarrative={setReportNarrative}
                  appendices={reportAppendices}
                  setAppendices={setReportAppendices}
                />
              </TabsContent>
            </Tabs>
          </div>
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
      <Toaster position="top-right" richColors />
    </ThemeProvider>
  );
}

export default App;
